import React, { useCallback, useEffect, useRef, useState } from 'react';
import CompaniesTable from './CompaniesTable';
import ButtonSaveForm from '../../components/forms/ButtonSaveForm';
import ButtonCancelForm from '../../components/forms/ButtonCancelForm';
import { showToast } from '../../utils/toast';
import ConfirmAlert from '../../components/Base/ConfirmAlert';
import CompaniesForm from '../../components/Companies/CompaniesForm';
import useCompaniesService from '../../services/useCompaniesService';
import { Company } from '../../models/CompanyModel';
import { useFormErrors } from '../../hooks/useFormErrors';
import Pagination from '../../components/Base/Pagination';
import TableTextSearch from '../../components/Base/TableTextSearch';
import MaintainerPageShell from '../../components/Base/MaintainerPageShell';
import { isAdministrator } from '../../hooks/usePermissions';
import { Building2, Plus } from 'lucide-react';

const CompaniesPage: React.FC = () => {
  const canEditOrDeleteCompanies = isAdministrator();
  const svc = useCompaniesService();
  const { list: getCompanies, store: createCompany, update: updateCompany, remove: removeCompany, fetching } = svc;

  const [companies, setCompanies] = useState<Company[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
    hasNext: false,
    hasPrevious: false,
  });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Company | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [savingLocked, setSavingLocked] = useState(false);
  const activeRequest = useRef<AbortController | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [companyIdToDelete, setCompanyIdToDelete] = useState<number | null>(null);
  const { fieldErrors, clearErrors, setErrorsFromResponse } = useFormErrors();
  const [searchText, setSearchText] = useState('');
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback((page: number = 1, search?: string) => {
    activeRequest.current?.abort();
    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    const filters: Record<string, unknown> = { page };
    const term = (search !== undefined ? search : searchTextRef.current).trim();
    if (term) filters.search = term;

    getCompanies(
      filters as Record<string, string | number>,
      {
        onSuccess: (resp) => {
          const data = resp?.data ?? resp ?? {};
          const items = Array.isArray(data.companies) ? data.companies : data.items ?? [];
          setCompanies(items);

          if (data.pagination) {
            setPagination({
              page: data.pagination.page || page,
              totalPages: data.pagination.total_pages || 1,
              totalCount: data.pagination.total_count || 0,
              pageSize: data.pagination.page_size || 10,
              hasNext: data.pagination.has_next || false,
              hasPrevious: data.pagination.has_previous || false,
            });
          }
        },
        onError: (resp) => {
          if (resp?.http_status === 403) {
            showToast(
              resp?.message || 'No tienes permisos para acceder a esta sección.',
              'error'
            );
          } else {
            showToast(resp?.message ?? 'Error cargando empresas', 'error');
          }
        },
      },
      ctrl.signal
    );
  }, [getCompanies]);

  useEffect(() => {
    load();
    return () => {
      activeRequest.current?.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [load]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchText(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => load(1, value), 400);
    },
    [load]
  );

  const openDeleteConfirmation = (id: number) => {
    setCompanyIdToDelete(id);
    setIsAlertOpen(true);
  };

  const openCreate = () => {
    setEditing({ name: '' });
    setFieldError(null);
    setShowForm(true);
  };

  const openEdit = (c: Company) => {
    setEditing(c);
    setFieldError(null);
    setShowForm(true);
  };

  const callbackSave = async (): Promise<any> => {
    if (!editing) return;

    setFieldError(null);
    setSavingLocked(true);
    try {
      if (editing.id) {
        const r = await updateCompany(editing.id, { name: editing.name });
        return r;
      } else {
        const r = await createCompany({ name: editing.name });
        return r;
      }
    } catch (error: any) {
      const nameErr =
        error?.errors?.items?.[0]?.name?.[0] ??
        error?.errors?.items?.['0']?.name?.[0];

      if (nameErr) {
        setErrorsFromResponse({ name: [nameErr] });
      }
    } finally {
      setSavingLocked(false);
    }
  };

  const handleSaveComplete = (ok: boolean, resp?: any) => {
    if (ok) {
      showToast(resp?.message ?? 'Guardado', 'success');
      setShowForm(false);
      setEditing(null);
      clearErrors();
      load(pagination.page, searchTextRef.current.trim() || undefined);
      return;
    }
    setErrorsFromResponse(resp);
    showToast(resp?.message ?? 'Error guardando', 'error');
  };

  const handleClose = () => {
    clearErrors();
    setShowForm(false);
    setFieldError(null);
  };

  const handleConfirmDelete = () => {
    if (companyIdToDelete === null) return;

    removeCompany(companyIdToDelete, {
      onSuccess: (r: any) => {
        showToast(r?.message ?? 'Eliminado', 'success');
        load(pagination.page, searchTextRef.current.trim() || undefined);
      },
      onError: (r: any) => showToast(r?.message ?? 'Error eliminando', 'error'),
    });

    setIsAlertOpen(false);
    setCompanyIdToDelete(null);
  };

  const handleCloseAlert = () => {
    setIsAlertOpen(false);
    setCompanyIdToDelete(null);
  };

  const totalLabel = fetching.list
    ? 'Cargando...'
    : (
      <span className="maintainer-page__meta-count">
        <span className="maintainer-page__meta-pill">
          {pagination.totalCount || companies.length}
        </span>
        empresas
      </span>
    );

  return (
    <MaintainerPageShell
      title="Empresas"
      icon={<Building2 size={22} strokeWidth={2.25} />}
      meta={totalLabel}
      actions={
        <button type="button" className="btn-create" onClick={openCreate}>
          <Plus size={16} strokeWidth={2.25} aria-hidden />
          Nueva empresa
        </button>
      }
    >
      <CompaniesTable
        data={companies}
        loading={fetching.list}
        onEdit={openEdit}
        onDelete={openDeleteConfirmation}
        showActions={canEditOrDeleteCompanies}
        totalCount={pagination.totalCount}
        searchTerm={searchText}
        onClearSearch={() => handleSearchChange('')}
        toolbar={
          <TableTextSearch
            value={searchText}
            onChange={handleSearchChange}
            placeholder="Buscar por nombre de empresa..."
            aria-label="Buscar empresas por nombre"
          />
        }
        footer={
          pagination.totalPages > 1 ? (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              onPageChange={(page) => {
                load(page, searchTextRef.current.trim() || undefined);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          ) : null
        }
      />

      {showForm && editing && (
        <div className="companies-modal">
          <div className="companies-modal-inner">
            <h3>{editing.id ? 'Editar empresa' : 'Crear empresa'}</h3>
            <CompaniesForm
              value={editing}
              onChange={setEditing}
              error={fieldError || undefined}
              fieldErrors={fieldErrors}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <ButtonCancelForm callbackCancel={handleClose} locked={savingLocked} confirmCancel={false} />
              <ButtonSaveForm callbackSave={callbackSave} locked={savingLocked} onComplete={handleSaveComplete} />
            </div>
          </div>
        </div>
      )}

      <ConfirmAlert
        isOpen={isAlertOpen}
        title="¿Eliminar empresa?"
        message="Esta acción es permanente y no se puede deshacer."
        onConfirm={handleConfirmDelete}
        onClose={handleCloseAlert}
      />
    </MaintainerPageShell>
  );
};

export default CompaniesPage;
